import type { VenueReservation } from "@/graphql/operations";
import { useCallback, useState } from "react";
import { deriveBucketState } from "./reservationsDerivedState";
import type {
  FetchedMonthsRange,
  ReservationBounds,
  ReservationsBucket,
} from "./reservationsTypes";

/**
 * Holds the active reservations UI state derived from buckets.
 */
export default function useReservationsState() {
  const [reservations, setReservations] = useState<VenueReservation[]>([]);
  const [totalReservations, setTotalReservations] = useState(0);
  const [reservationsStart, setReservationsStart] = useState(0);
  const [reservationsEnd, setReservationsEnd] = useState(-1);
  const [reachedPastEnd, setReachedPastEnd] = useState(false);
  const [reachedUpcomingEnd, setReachedUpcomingEnd] = useState(false);
  const [reservationBounds, setReservationBounds] =
    useState<ReservationBounds>({
      earliestDate: null,
      latestDate: null,
      resolved: false,
    });
  const [fetchedMonthsRange, setFetchedMonthsRange] =
    useState<FetchedMonthsRange>({ from: null, until: null });

  /**
   * Applies derived bucket state to local state setters.
   */
  const applyBucketState = useCallback((bucket: ReservationsBucket) => {
    const derived = deriveBucketState(bucket);
    setReservations(derived.reservations);
    setTotalReservations(derived.totalReservations);
    setReservationsStart(derived.reservationsStart);
    setReservationsEnd(derived.reservationsEnd);
    setReachedPastEnd(derived.reachedPastEnd);
    setReachedUpcomingEnd(derived.reachedUpcomingEnd);
    setFetchedMonthsRange(derived.fetchedMonthsRange);
    setReservationBounds(derived.reservationBounds);
  }, []);

  return {
    reservations,
    totalReservations,
    reservationsStart,
    reservationsEnd,
    reachedPastEnd,
    reachedUpcomingEnd,
    reservationBounds,
    fetchedMonthsRange,
    applyBucketState,
  };
}
